import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { config } from "../../../../../Helpers/env";

/**
 * useANCContact — Custom hook for ANC Clinical Assessment form state,
 * auto-calculations, danger-sign alerts, and contact history.
 *
 * Handles:
 *  - Form state management for all ANC contact fields
 *  - Auto-calculation of gestational age from LNMP
 *  - Auto-calculation of BMI from weight + height
 *  - MUAC color coding (green/yellow/red)
 *  - Danger sign auto-alerts (BP, Hb, fetal HR, temp, proteinuria)
 *  - Fetching booking data and previous contacts
 *  - Submission of new contact records
 *  - Mock data support for UI development
 */

/* ═══ MOCK DATA FLAG ═══ */
const USE_MOCK_DATA = false;


export const useANCContact = (actions, patient_number, visit_id) => {
    /* ─── Booking data (fetched from ANC booking) ─── */
    const [bookingData, setBookingData] = useState(null);
    const [loadingBooking, setLoadingBooking] = useState(true);

    /* ─── Previous contacts (timeline) ─── */
    const [contacts, setContacts] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(false);

    /* ─── Active view: "form" or "timeline" ─── */
    const [activeView, setActiveView] = useState("form");

    /* ─── Viewing a specific past contact ─── */
    const [selectedContact, setSelectedContact] = useState(null);

    /* ─── Form submission state ─── */
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);

    /* ─── Section collapse state (managed here for persistence) ─── */
    const [collapsedSections, setCollapsedSections] = useState({
        vitals: false,
        abdominal: false,
        urinalysis: false,
        notes: false,
    });

    const toggleSection = useCallback((section) => {
        setCollapsedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    }, []);

    /* ─── Contact form data ─── */
    const [contactData, setContactData] = useState({
        contact_number: "",
        contact_date: new Date().toISOString().slice(0, 10),
        // Vitals
        weight_kg: "",
        muac_cm: "",
        blood_pressure_systolic: "",
        blood_pressure_diastolic: "",
        pulse_rate: "",
        temperature: "",
        respiratory_rate: "",
        // Abdominal Exam
        fundal_height_cm: "",
        fetal_heart_rate: "",
        fetal_presentation: "",
        fetal_position: "",
        presenting_part_to_brim: "",
        oedema: "none",
        // Urinalysis
        urine_protein: "nil",
        urine_glucose: "nil",
        haemoglobin: "",
        // Clinical Notes
        complaints: "",
        clinical_notes: "",
        next_appointment_date: "",
    });

    /* ─── Fetch ANC booking on mount ─── */
    useEffect(() => {
        if (!patient_number) return;
        setLoadingBooking(true);

        axios
            .get(`${config.smsUrl}/obgyn/anc/booking/patient/${patient_number}`)
            .then((res) => {
                const data = res.data || {};
                if (data.id) {
                    setBookingData(data);
                    const nextContact = (data.contacts_done || 0) + 1;
                    setContactData((prev) => ({
                        ...prev,
                        contact_number: nextContact > 8 ? 8 : nextContact,
                    }));
                }
            })
            .catch(() => {
                setBookingData(null);
            })
            .finally(() => setLoadingBooking(false));
    }, [patient_number]);

    /* ─── Fetch previous contacts for timeline ─── */
    useEffect(() => {
        if (!bookingData?.id) return;
        setLoadingContacts(true);

        axios
            .get(`${config.smsUrl}/obgyn/anc/contacts/${bookingData.id}`)
            .then((res) => {
                const data = res.data || [];
                setContacts(Array.isArray(data) ? data : []);
            })
            .catch(() => setContacts([]))
            .finally(() => setLoadingContacts(false));
    }, [bookingData]);

    /* ─── Handle form field changes ─── */
    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setContactData((prev) => ({ ...prev, [name]: value }));
    }, []);

    /* ═══ AUTO-CALCULATIONS ═══ */

    /** Gestational age (weeks+days) from booking LNMP */
    const gestationalAge = useMemo(() => {
        if (!bookingData?.lnmp) return null;
        const lnmpDate = new Date(bookingData.lnmp);
        const contactDate = contactData.contact_date
            ? new Date(contactData.contact_date)
            : new Date();
        const diffMs = contactDate - lnmpDate;
        if (diffMs < 0) return null;
        const totalDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
        const weeks = Math.floor(totalDays / 7);
        const days = totalDays % 7;
        const trimester = weeks < 14 ? 1 : weeks < 28 ? 2 : 3;
        const progressPercent = Math.min(100, Math.round((totalDays / 280) * 100));
        return { weeks, days, totalDays, trimester, progressPercent, display: `${weeks}w ${days}d` };
    }, [bookingData?.lnmp, contactData.contact_date]);

    /** Days remaining to EDD */
    const daysToEDD = useMemo(() => {
        if (!bookingData?.edd) return null;
        const eddDate = new Date(bookingData.edd);
        const now = new Date();
        const diffMs = eddDate - now;
        return Math.max(0, Math.ceil(diffMs / (24 * 60 * 60 * 1000)));
    }, [bookingData?.edd]);

    /** BMI from weight + booking height */
    const bmi = useMemo(() => {
        const weight = parseFloat(contactData.weight_kg);
        const heightCm = parseFloat(bookingData?.height_cm);
        if (!weight || !heightCm) return null;
        const heightM = heightCm / 100;
        const val = weight / (heightM * heightM);
        return {
            value: val.toFixed(1),
            category:
                val < 18.5
                    ? "Underweight"
                    : val < 25
                    ? "Normal"
                    : val < 30
                    ? "Overweight"
                    : "Obese",
            color:
                val < 18.5
                    ? "#e65100"
                    : val < 25
                    ? "#2e7d32"
                    : val < 30
                    ? "#f57f17"
                    : "#c62828",
        };
    }, [contactData.weight_kg, bookingData?.height_cm]);

    /** MUAC color coding */
    const muacStatus = useMemo(() => {
        const muac = parseFloat(contactData.muac_cm);
        if (!muac) return null;
        if (muac >= 23) return { color: "#2e7d32", bg: "#e8f5e9", label: "Normal", code: "green" };
        if (muac >= 21) return { color: "#f57f17", bg: "#fff8e1", label: "At Risk", code: "yellow" };
        return { color: "#c62828", bg: "#ffebee", label: "Malnourished", code: "red" };
    }, [contactData.muac_cm]);

    /** Weight gain trend (from previous contacts) */
    const weightTrend = useMemo(() => {
        if (!contacts.length || !contactData.weight_kg) return null;
        const lastWeight = parseFloat(contacts[0]?.weight_kg);
        const currentWeight = parseFloat(contactData.weight_kg);
        if (!lastWeight || !currentWeight) return null;
        const diff = currentWeight - lastWeight;
        return {
            diff: diff.toFixed(1),
            direction: diff > 0 ? "up" : diff < 0 ? "down" : "stable",
            color: diff > 0 ? "#2e7d32" : diff < 0 ? "#e65100" : "#666",
            label: diff > 0 ? `+${diff.toFixed(1)} kg` : `${diff.toFixed(1)} kg`,
        };
    }, [contacts, contactData.weight_kg]);

    /* ═══ FORM COMPLETION TRACKING ═══ */
    const formProgress = useMemo(() => {
        const sections = {
            vitals: {
                filled: [
                    contactData.weight_kg,
                    contactData.muac_cm,
                    contactData.blood_pressure_systolic,
                    contactData.pulse_rate,
                    contactData.temperature,
                    contactData.respiratory_rate,
                ].filter(Boolean).length,
                total: 6,
            },
            abdominal: {
                filled: [
                    contactData.fundal_height_cm,
                    contactData.fetal_heart_rate,
                    contactData.fetal_presentation,
                    contactData.oedema !== "none" && contactData.oedema,
                ].filter(Boolean).length,
                total: 4,
            },
            urinalysis: {
                filled: [
                    contactData.urine_protein !== "nil" && contactData.urine_protein,
                    contactData.urine_glucose !== "nil" && contactData.urine_glucose,
                    contactData.haemoglobin,
                ].filter(Boolean).length,
                total: 3,
            },
            notes: {
                filled: [contactData.complaints, contactData.clinical_notes].filter(Boolean).length,
                total: 2,
            },
        };
        const totalFilled = Object.values(sections).reduce((a, s) => a + s.filled, 0);
        const totalFields = Object.values(sections).reduce((a, s) => a + s.total, 0);
        return {
            sections,
            totalFilled,
            totalFields,
            percent: Math.round((totalFilled / totalFields) * 100),
        };
    }, [contactData]);

    /* ═══ DANGER SIGN ALERTS ═══ */
    const dangerSigns = useMemo(() => {
        const alerts = [];
        const sys = parseFloat(contactData.blood_pressure_systolic);
        const dia = parseFloat(contactData.blood_pressure_diastolic);
        const hb = parseFloat(contactData.haemoglobin);
        const fhr = parseFloat(contactData.fetal_heart_rate);
        const temp = parseFloat(contactData.temperature);
        const protein = contactData.urine_protein;

        // Hypertension check
        if (sys >= 140 || dia >= 90) {
            alerts.push({
                type: sys >= 160 || dia >= 110 ? "critical" : "warning",
                icon: "⚠️",
                title: "Elevated Blood Pressure",
                detail: `BP ${sys || "—"}/${dia || "—"} mmHg — ${
                    sys >= 160 || dia >= 110
                        ? "Severe hypertension — URGENT"
                        : "Hypertension detected"
                }`,
                action: "Evaluate for pre-eclampsia. Check proteinuria.",
            });
        }

        // Pre-eclampsia (BP + proteinuria)
        if ((sys >= 140 || dia >= 90) && (protein === "++" || protein === "+++")) {
            alerts.push({
                type: "critical",
                icon: "🚨",
                title: "Possible Pre-eclampsia",
                detail: `BP ${sys || "—"}/${dia || "—"} with proteinuria ${protein}`,
                action: "URGENT: Assess for pre-eclampsia. Consider MgSO4 and referral.",
            });
        }

        // Anaemia
        if (hb && hb < 11) {
            alerts.push({
                type: hb < 7 ? "critical" : "warning",
                icon: hb < 7 ? "🚨" : "⚠️",
                title: hb < 7 ? "Severe Anaemia" : "Anaemia Detected",
                detail: `Haemoglobin ${hb} g/dL — ${
                    hb < 7 ? "Severe" : hb < 10 ? "Moderate" : "Mild"
                } anaemia`,
                action:
                    hb < 7
                        ? "URGENT: Consider transfusion and referral"
                        : "Increase iron/folic acid dosage. Follow up in 2 weeks.",
            });
        }

        // Fetal heart rate abnormalities
        if (fhr && (fhr < 110 || fhr > 160)) {
            alerts.push({
                type: "critical",
                icon: "💗",
                title: "Abnormal Fetal Heart Rate",
                detail: `FHR ${fhr} bpm — ${
                    fhr < 110 ? "Fetal bradycardia" : "Fetal tachycardia"
                }`,
                action: "Continuous monitoring. Assess fetal well-being. Consider NST.",
            });
        }

        // Fever
        if (temp && temp >= 37.5) {
            alerts.push({
                type: temp >= 38.5 ? "critical" : "warning",
                icon: "🌡️",
                title: "Fever Detected",
                detail: `Temperature ${temp}°C`,
                action: "Investigate cause: malaria RDT, urinalysis, blood culture if indicated.",
            });
        }

        // Reduced fetal movements (check gestational age)
        if (gestationalAge && gestationalAge.weeks >= 28 && contactData.complaints &&
            contactData.complaints.toLowerCase().includes("reduced") &&
            contactData.complaints.toLowerCase().includes("movement")) {
            alerts.push({
                type: "warning",
                icon: "🤰",
                title: "Possible Reduced Fetal Movements",
                detail: "Patient reports reduced fetal movements",
                action: "Perform kick count chart. Consider NST/CTG. Assess amniotic fluid.",
            });
        }

        return alerts;
    }, [
        contactData.blood_pressure_systolic,
        contactData.blood_pressure_diastolic,
        contactData.haemoglobin,
        contactData.fetal_heart_rate,
        contactData.temperature,
        contactData.urine_protein,
        contactData.complaints,
        gestationalAge,
    ]);

    /* ─── Submit contact form ─── */
    const handleSubmit = useCallback(
        (e) => {
            if (e) e.preventDefault();
            setSubmitted(true);

            if (
                !contactData.contact_date ||
                !contactData.weight_kg ||
                !contactData.blood_pressure_systolic ||
                !contactData.blood_pressure_diastolic
            ) {
                actions?.snackbarActions?.snackError("Please fill in all required fields");
                return;
            }

            if (!bookingData?.id) {
                actions?.snackbarActions?.snackError(
                "No active ANC booking found. Please register the patient first."
            );
                return;
            }

            setSaving(true);
            const payload = {
                ...contactData,
                booking_id: bookingData.id,
                visit_id: visit_id,
                gestational_age_weeks: gestationalAge?.weeks || null,
                bmi: bmi?.value || null,
                muac_color_code: muacStatus?.code || null,
            };

            axios
                .post(`${config.smsUrl}/obgyn/anc/contact`, payload)
                .then((res) => {
                    actions?.snackbarActions?.snackSuccess("ANC contact recorded successfully");
                    setContacts((prev) => [res.data || payload, ...prev]);
                    resetForm();
                })
                .catch((err) => {
                    const msg =
                        err?.response?.data?.message || "Failed to save ANC contact";
                    actions?.snackbarActions?.snackError(msg);
                })
                .finally(() => setSaving(false));
        },
        [contactData, bookingData, visit_id, gestationalAge, bmi, muacStatus, actions]
    );

    /* ─── Reset form ─── */
    const resetForm = useCallback(() => {
        setSubmitted(false);
        setContactData({
            contact_number: (contacts.length || 0) + 2 > 8 ? 8 : (contacts.length || 0) + 2,
            contact_date: new Date().toISOString().slice(0, 10),
            weight_kg: "",
            muac_cm: "",
            blood_pressure_systolic: "",
            blood_pressure_diastolic: "",
            pulse_rate: "",
            temperature: "",
            respiratory_rate: "",
            fundal_height_cm: "",
            fetal_heart_rate: "",
            fetal_presentation: "",
            fetal_position: "",
            presenting_part_to_brim: "",
            oedema: "none",
            urine_protein: "nil",
            urine_glucose: "nil",
            haemoglobin: "",
            complaints: "",
            clinical_notes: "",
            next_appointment_date: "",
        });
    }, [contacts.length]);

    /* ─── View a specific past contact in detail ─── */
    const viewContact = useCallback((contact) => {
        setSelectedContact(contact);
        setActiveView("detail");
    }, []);

    const closeContactDetail = useCallback(() => {
        setSelectedContact(null);
        setActiveView("timeline");
    }, []);

    return {
        // Booking
        bookingData,
        loadingBooking,
        // Form
        contactData,
        handleChange,
        handleSubmit,
        resetForm,
        submitted,
        saving,
        // Auto-calculations
        gestationalAge,
        daysToEDD,
        bmi,
        muacStatus,
        weightTrend,
        // Danger signs
        dangerSigns,
        // Contacts / Timeline
        contacts,
        loadingContacts,
        // View state
        activeView,
        setActiveView,
        selectedContact,
        viewContact,
        closeContactDetail,
        // Section collapse
        collapsedSections,
        toggleSection,
        // Form progress
        formProgress,
    };
};
